import Cookies from 'js-cookie';

import { MESSAGE_TYPES } from '@/lib/constants/widget-events';
import { isInIframe } from '@/lib/utils/dom';
import { setForceThreadRestore } from '@/lib/utils/thread';

export { getTextDirection, isRTLLocale } from './locale-shared';

const PREFERRED_LOCALE_COOKIE = 'preferred-locale';
const ONE_YEAR_DAYS = 365;

// Cross-site iframes need `SameSite=None; Secure; Partitioned` (CHIPS) for
// the cookie to be accepted as partitioned third-party state; HTTP dev
// degrades to `SameSite=Lax` since `Secure` would be rejected.
// `manualChoiceInMemory` is a per-iframe-lifetime fallback for contexts that
// reject cookies entirely — survives thread switches, lost on full reload.
let manualChoiceInMemory = false;

function writePreferredLocaleCookie(locale: string) {
  // `partitioned` isn't in js-cookie's typed attribute surface, but its writer
  // (`dist/js.cookie.mjs`) iterates arbitrary attribute keys and emits them
  // verbatim — so passing `partitioned: true` yields `; partitioned` in the
  // cookie string, which is exactly what CHIPS requires.
  const isHttps = window.location.protocol === 'https:';
  Cookies.set(PREFERRED_LOCALE_COOKIE, locale, {
    path: '/',
    expires: ONE_YEAR_DAYS,
    sameSite: isHttps ? 'None' : 'Lax',
    secure: isHttps,
    ...(isHttps && { partitioned: true }),
  } as Cookies.CookieAttributes & { partitioned?: boolean });
}

export function hasManualLocaleChoice(): boolean {
  return !!Cookies.get(PREFERRED_LOCALE_COOKIE) || manualChoiceInMemory;
}

// The widget loader / tooltip-rendering script on the parent origin is loaded
// with `crossOrigin="anonymous"`, which strips credentials — cookies never
// reach those requests. Notify the parent so its first-party `localStorage`
// can persist the choice and surface it as a `locale=` query param on the
// script URL on subsequent visits; otherwise the tooltip would fall back to
// the host-inferred locale after a full page close/reopen, and could render
// in a different language than the chat iframe. Fired on every manual choice
// (confirm, keep-current, language-selector) so the parent stays in sync.
function notifyParentOfLocaleChoice(locale: string) {
  if (!isInIframe()) return;
  try {
    window.parent.postMessage({ type: MESSAGE_TYPES.LOCALE_CHANGED, locale }, '*');
  } catch {
    // parent may be inaccessible under strict COOP/COEP — best-effort only
  }
}

export function markLocaleAsManuallyChosen(locale: string) {
  manualChoiceInMemory = true;
  writePreferredLocaleCookie(locale);
  notifyParentOfLocaleChoice(locale);
}

export function changeLocale(newLocale: string) {
  setForceThreadRestore();
  markLocaleAsManuallyChosen(newLocale);
  window.location.reload();
}
